import { ReactNode, useState } from "react";
import EmbedContainer from "~/components/EmbedContainer";
import EmbedOverlay from "~/components/EmbedOverlay";
import { useFetcher } from "~/hooks/useFetcher";
import monitoring from "~/utils/monitoring";

const { VITE_ACCESS_CONTROLLER_API, VITE_SIGMA_URL } = import.meta.env;

const getAccessControllerUrl = (workbookUrl: string): string | null => {
  try {
    const url = new URL(workbookUrl);
    let sourceUrl = `${url.origin}${url.pathname}`;
    const searchParams = new URLSearchParams(url.search);
    const selectedNode = url.searchParams.get(":nodeId");
    if (selectedNode && !sourceUrl.includes("/edit") && !sourceUrl.includes("/page/")) {
      // Page element is selected in url using query string instead of context path, reformat for request
      sourceUrl += `/page/${selectedNode}`;
      // Remove nodeId query param since we're loading the workbook as a single page
      searchParams.delete(":nodeId");
    }

    // Add remaining query parameters to the sourceUrl
    const remainingParams = searchParams.toString();
    if (remainingParams) {
      sourceUrl += `?${remainingParams}`;
    }

    return `${VITE_ACCESS_CONTROLLER_API}/users/sigma/embed-url?source_url=${encodeURIComponent(
      sourceUrl
    )}`;
  } catch (e) {
    console.error(e);
    monitoring?.recordError(e);
  }
  return null;
};

interface SigmaEmbed {
  title?: ReactNode;
  workbookUrl?: string;
  inline?: boolean;
  onClose?: () => void;
  messageHandler?: (event: MessageEvent) => void;
}

const SigmaEmbed = ({ title, workbookUrl, inline, onClose, messageHandler }: SigmaEmbed) => {
  const [loading, setLoading] = useState(true);
  const refreshInterval = 24 * 60 * 60 * 1000;
  const leeway = 5 * 1000;
  const { data: sigmaEmbedUrl } = useFetcher<{ url: string }>(
    workbookUrl ? getAccessControllerUrl(workbookUrl) : null,
    {
      // Refresh every 24 hours (with 5 seconds of leeway)
      refreshInterval: refreshInterval - leeway,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    }
  );

  const handleLoadingEvent = (e: MessageEvent) => {
    if (
      e.origin === VITE_SIGMA_URL &&
      ["workbook:loaded", "workbook:error"].includes(e.data.type)
    ) {
      setLoading(false);
    }
  };

  const props = {
    key: workbookUrl,
    title,
    loading,
    iframeSrc: sigmaEmbedUrl?.url,
    messageHandler: (e: MessageEvent) => {
      handleLoadingEvent(e);
      if (messageHandler) {
        messageHandler(e);
      }
    },
  };

  if (inline) {
    return <EmbedContainer {...props} />;
  }

  if (!onClose) {
    throw Error("onClose handler required for Sigma overlay modal");
  }

  return <EmbedOverlay hideOpenInNewTab onClose={onClose} {...props} />;
};

export default SigmaEmbed;
